import styled from '@emotion/styled';
import { Button, Form, FormInputTextarea, useForm } from '@modernhealth/vital-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { color, font } from 'src/shared/styles/variables';

const HeaderText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 22,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

interface Props {
    onContinueClick: () => void;
    onTextChange: (text: string) => void;
    onSkipClick?: () => void;
    textValue: string;
    loading: boolean;
    isPamm?: boolean;
}

interface FormData {
    feedbackText: string;
}

export const RateSessionTextFeedbackStep: React.FC<Props> = ({
    onContinueClick,
    onTextChange,
    onSkipClick,
    textValue,
    loading,
    isPamm,
}) => {
    const { t } = useTranslation();
    const flags = useFlags();

    const title =
        flags?.showAlternateFeedbackCopy || isPamm
            ? 'rate_session.text_feedback.feedback'
            : 'rate_session.text_feedback.private_feedback';
    const placeholder =
        flags?.showAlternateFeedbackCopy || isPamm
            ? 'rate_session.text_feedback.feedback.placeholder'
            : 'rate_session.text_feedback.private_feedback.placeholder';

    const formDefinition = useForm<FormData>();

    const registerFeedback = formDefinition.register('feedbackText', {
        onChange: event => onTextChange(event.target.value),
    });

    return (
        <React.Fragment>
            <Form definition={formDefinition} css={{ gap: 0 }}>
                <HeaderText id="feedback-label">{t(`videoSessions:${title}`)}</HeaderText>

                <FormInputTextarea
                    rows={8}
                    placeholder={t(`videoSessions:${placeholder}`)}
                    labelledBy="feedback-label"
                    defaultValue={textValue}
                    {...registerFeedback}
                />

                {isPamm && (
                    <FirstButtonWrapper>
                        <Button
                            onClick={onSkipClick || onContinueClick}
                            fluid
                            size="large"
                            variant="flat"
                        >
                            {t('common.skip')}
                        </Button>
                    </FirstButtonWrapper>
                )}
                <SecondButtonWrapper>
                    <Button
                        onClick={onContinueClick}
                        fluid
                        disabled={isPamm && textValue.length === 0}
                        busy={loading}
                        size="large"
                    >
                        {t('common.continue')}
                    </Button>
                </SecondButtonWrapper>
            </Form>
        </React.Fragment>
    );
};

const FirstButtonWrapper = styled.div({
    marginTop: 35,
    width: '100%',
});

const SecondButtonWrapper = styled.div({
    marginTop: 10,
    width: '100%',
});
